import Head from 'next/head';
import {useRouter} from 'next/router';
import React from 'react';

import {GwgEnrollmentProvider} from '@udemy/career-academies';
import {HeadSEO} from '@udemy/head-seo';
import {useI18n} from '@udemy/i18n';
import {SubscriptionPlansProvider, SubscriptionPlanProductType} from '@udemy/subscription-graphql';

import {useSetupNavigation} from 'src/hooks/use-setup-navigation';
import {useCanonicalUrl} from 'src/hooks/useCanonicalUrl';
import {META_DEFAULT_BASE_URL} from 'src/lib/constants';
import {getOgLocale} from 'src/lib/get-og-locale';
import {PageProps} from 'src/types/page-props';

import {GoogleAiPlanPage, GoogleAiPlanPageProps} from './google-ai-plan-page.react-component';

export type GoogleAiPlanPageFeatureProps = PageProps & GoogleAiPlanPageProps;

export function GoogleAiPlanPageFeature(props: GoogleAiPlanPageFeatureProps) {
    const router = useRouter();
    const {gettext} = useI18n();
    const {url: canonicalUrl} = useCanonicalUrl([], META_DEFAULT_BASE_URL);
    useSetupNavigation();

    const metaProps = {
        title: gettext('Google AI Plan | Udemy'),
        description: gettext('Explore Google AI learning plans and courses on Udemy.'),
    };

    const ogLocale = getOgLocale(props.locale);

    // Extract accelerator IDs for enrollment context
    const acceleratorIds = React.useMemo(() => {
        return props.allGwgAccelerators?.map((acc) => acc.numericId.toString()) ?? [];
    }, [props.allGwgAccelerators]);

    return (
        <>
            <HeadSEO
                title={metaProps.title}
                ogTitle={metaProps.title}
                description={metaProps.description}
                ogDescription={metaProps.description}
                ogLocale={ogLocale}
                canonicalUrl={canonicalUrl}
                assetPath={process.env.ASSET_PREFIX}
                headWrapper={Head}
            />
            <SubscriptionPlansProvider
                subscriptionPlanQueryVariables={[
                    {
                        filters: {
                            productType: SubscriptionPlanProductType.Consumersubscription,
                        },
                        marketingURL: `${new URL(canonicalUrl).origin}${router?.asPath}`,
                    },
                    {
                        filters: {
                            productType: SubscriptionPlanProductType.GrowWithGoogle,
                        },
                        marketingURL: `${new URL(canonicalUrl).origin}${router?.asPath}`,
                    },
                    {
                        filters: {
                            productType:
                                SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle,
                        },
                        marketingURL: `${new URL(canonicalUrl).origin}${router?.asPath}`,
                    },
                ]}
            >
                <GwgEnrollmentProvider acceleratorIds={acceleratorIds}>
                    <GoogleAiPlanPage
                        enrollmentsData={props.enrollmentsData}
                        plan={props.plan}
                        instructor={props.instructor}
                        allGwgAccelerators={props.allGwgAccelerators}
                    />
                </GwgEnrollmentProvider>
            </SubscriptionPlansProvider>
        </>
    );
}
